/**
+ * User Permissions API Functions
+ * Handles user permission inquiry functionality
+ */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../../auth/authUtils';
import { getApiGateway } from '../../api/endpointUtils';
import type {
  GetUserPermissionsParams,
  GetUserPermissionsResponse,
  GetTargetUserPermissionsParams,
  GetTargetUserPermissionsResponse,
  SetTargetUserPermissionsParams,
  SetTargetUserPermissionsResponse,
  UserPermissions
} from '../types';
import { 
  GET_USER_PERMISSIONS_ERROR_MESSAGES, 
  GET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES,
  SET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES
} from '../types';

/**
 * Get master permissions for the authenticated user
 * Retrieves all permissions and access rights for the current user
 * 
 * @param params - Permission query parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with master permissions response
 */
export async function getMasterPermissions(params: Omit<GetUserPermissionsParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
} = {}): Promise<GetUserPermissionsResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetUserPermissionsParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('email', requestParams.email);
    queryParams.append('user_token', requestParams.user_token);
    queryParams.append('tokenType', requestParams.tokenType || 'web');

    console.log('🔐 [Permissions API] Requesting user permissions:', {
      email: requestParams.email,
      hasUserToken: !!requestParams.user_token,
      tokenType: requestParams.tokenType
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/UserPermissions?${queryParams.toString()}`, {
      method: 'GET',
//      headers: {
//       'Authorization': `Bearer ${authConfig.jwtToken}`,
//     },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetUserPermissionsResponse = await response.json();

    console.log('🔐 [Permissions API] API response received:', {
      resultcode: data.resultcode,
      hasPermissions: !!data.response?.permissions,
      permissionCount: data.response?.permissions ? Object.keys(data.response.permissions).length : 0
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_USER_PERMISSIONS_ERROR_MESSAGES[data.resultcode as keyof typeof GET_USER_PERMISSIONS_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Permissions API] User permissions request failed:', error);
    throw error;
  }
}

/**
 * Get user permissions for SubMaster and Users
 * Retrieves permissions and access rights for SubMaster and User roles
 * 
 * @param params - Permission query parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with user permissions response
 */
export async function getUserPermissions(params: Omit<GetUserPermissionsParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
} = {}): Promise<GetUserPermissionsResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetUserPermissionsParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('email', requestParams.email);
    queryParams.append('user_token', requestParams.user_token);
    queryParams.append('tokenType', requestParams.tokenType || 'web');

    console.log('🔐 [Permissions API] Requesting user permissions (SubMaster/User):', {
      email: requestParams.email,
      hasUserToken: !!requestParams.user_token,
      tokenType: requestParams.tokenType
    });

    // Make API request to UserRole endpoint
    const response = await fetch(`${API_GATEWAY}/Account/UserRole?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetUserPermissionsResponse = await response.json();

    console.log('🔐 [Permissions API] API response received:', {
      resultcode: data.resultcode,
      hasPermissions: !!data.response?.permissions,
      permissionCount: data.response?.permissions ? Object.keys(data.response.permissions).length : 0
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_USER_PERMISSIONS_ERROR_MESSAGES[data.resultcode as keyof typeof GET_USER_PERMISSIONS_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Permissions API] User permissions request failed:', error);
    throw error;
  }
}

/**
 * Get target user permissions (Master queries SubMaster/User permissions)
 * Allows Master to check permissions of specific SubMaster or User
 * 
 * @param params - Target permission query parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with target user permissions response
 */
export async function getTargetUserPermissions(params: Omit<GetTargetUserPermissionsParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<GetTargetUserPermissionsResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: GetTargetUserPermissionsParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.targetEmail || requestParams.targetEmail.trim().length === 0) {
      throw new Error('Target email is required');
    }

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('email', requestParams.email);
    queryParams.append('user_token', requestParams.user_token);
    queryParams.append('tokenType', requestParams.tokenType || 'web');
    queryParams.append('targetEmail', requestParams.targetEmail);

    console.log('🔐 [Permissions API] Requesting target user permissions:', {
      email: requestParams.email,
      targetEmail: requestParams.targetEmail,
      hasUserToken: !!requestParams.user_token,
      tokenType: requestParams.tokenType
    });

    // Make API request to UserRoleTargetInfo endpoint
    const response = await fetch(`${API_GATEWAY}/Account/UserRoleTargetInfo?${queryParams.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: GetTargetUserPermissionsResponse = await response.json();

    console.log('🔐 [Permissions API] Target user permissions response received:', {
      resultcode: data.resultcode,
      targetEmail: requestParams.targetEmail,
      hasPermissions: !!data.response?.permissions,
      permissionCount: data.response?.permissions ? Object.keys(data.response.permissions).length : 0
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES[data.resultcode as keyof typeof GET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Permissions API] Target user permissions request failed:', error);
    throw error;
  }
}

/**
 * Set target user permissions (Master sets SubMaster/User permissions)
 * Allows Master to configure permissions for specific SubMaster or User
 * 
 * @param params - Target permission set parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with target user permissions set response
 */
export async function setTargetUserPermissions(params: Omit<import('../types').SetTargetUserPermissionsParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<SetTargetUserPermissionsResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: SetTargetUserPermissionsParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.roleSetEmail || requestParams.roleSetEmail.trim().length === 0) {
      throw new Error('Role set email is required');
    }
    
    if (!requestParams.permissions) {
      throw new Error('Permissions object is required');
    }

    console.log('🔐 [Permissions API] Setting target user permissions:', {
      email: requestParams.email,
      roleSetEmail: requestParams.roleSetEmail,
      hasUserToken: !!requestParams.user_token,
      tokenType: requestParams.tokenType,
      permissionCount: Object.keys(requestParams.permissions).length
    });

    // Make API request to UserRoleSet endpoint
    const response = await fetch(`${API_GATEWAY}/Account/UserRoleSet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: SetTargetUserPermissionsResponse = await response.json();

    console.log('🔐 [Permissions API] Set target user permissions response received:', {
      resultcode: data.resultcode,
      roleSetEmail: requestParams.roleSetEmail,
      hasResponse: !!data.response
    });

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = SET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES[data.resultcode as keyof typeof SET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    console.error('❌ [Permissions API] Set target user permissions request failed:', error);
    throw error;
  }
}

/**
 * Check if user has specific permission
 * Convenience function to check a single permission
 * 
 * @param permissionKey - Permission key to check
 * @param userType - User type to check permissions for (default: 'master')
 * @returns Promise with boolean result
 */
export async function hasPermission(
  permissionKey: keyof UserPermissions, 
  userType: 'master' | 'user' = 'master'
): Promise<boolean> {
  try {
    const response = userType === 'master' 
      ? await getMasterPermissions()
      : await getUserPermissions();
    
    if (!response.response?.permissions) {
      return false;
    }
    
    const permission = response.response.permissions[permissionKey];
    
    // Handle different permission types
    if (typeof permission === 'boolean') {
      return permission;
    } else if (typeof permission === 'number') {
      // For numeric permissions like liveView: 0 = unlimited, -1 = disabled, >0 = limited
      return permission >= 0;
    }
    
    return false;
    
  } catch (error) {
    console.error(`❌ [Permissions API] Failed to check permission ${permissionKey}:`, error);
    return false;
  }
}


/**
 * Check if permissions API is ready (authentication configured)
 * @returns True if authentication is configured
 */
export function isPermissionsApiReady(): boolean {
  return getAuthConfigFromStore() !== null;
}